import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import {
  expandDateKeyCandidates,
  getOnlyOneKey,
  groupRawEventListByTypeForModal,
} from '@/pages/dashboard/components/EventMetricTrend/kpiEventUiUtils';
import {
  isEventTypeExcluded,
  normalizeExcludeEventTypes,
} from '@/pages/dashboard/components/EventMetricTrend/trendEventListFilters';
import type {
  EventDataMap,
  TEventTypeBarColorMapConfig,
} from '@/pages/dashboard/components/EventMetricTrend/types';
import {
  applyEventTypeBarColorMapToEventDataMap,
  canonicalEventDateYyyymmdd,
  resolveEventTypeDisplayOrderKey,
} from '@/pages/dashboard/components/EventMetricTrend/utils';
import { normalizeRecordForEventWindowsDisplay } from '@/pages/dashboard/components/EventMetricTrend/eventRecordDisplayUtils';
import { buildPropsEventDataFromEventList } from '@/pages/dashboard/pages/overview/kpiOverview/components/KpiEventMetricTrendService/_utils';

/** resolveFullScreenExcludeEventTypes 所需的最小配置形态 */
export interface IFullScreenExcludeEventTypesConfig {
  excludeEventTypes?: string[];
}

export type TOverviewEventDateType = 'daily' | 'weekly' | 'monthly' | 'hourly';

/**
 * 页面筛选 formValue 指纹：日期/粒度等变化时用于重置全屏事件勾选态。
 */
export function buildFormValueScopeKey(
  formValue: Record<string, unknown> | undefined,
): string {
  if (!formValue) {
    return '';
  }
  try {
    return JSON.stringify(formValue);
  } catch {
    return '';
  }
}

/**
 * 全屏事件弹窗 data 指纹（按 event_type 分组 + getOnlyOneKey），与 EventSettingModal dataResetKey 同源。
 */
export function buildEventModalDataScopeKey(
  modalData: Record<string, unknown[]>,
): string {
  return JSON.stringify(
    Object.keys(modalData || {})
      .sort()
      .map(k =>
        (modalData[k] ?? []).map((row: Record<string, unknown>) =>
          getOnlyOneKey(row),
        ),
      ),
  );
}

/** 概览筛选 timeType（Day / Week / Month）→ EventWindows / KpiEventFilter 的 dateType */
export function resolveOverviewEventDateType(
  timeType?: string,
): TOverviewEventDateType {
  const t = String(timeType ?? '').toLowerCase();
  if (t === 'week') {
    return 'weekly';
  }
  if (t === 'month') {
    return 'monthly';
  }
  if (t === 'hour') {
    return 'hourly';
  }
  return 'daily';
}

/** 与趋势图 X 轴类目格式一致 */
export function resolveOverviewEventDateFormat(
  timeType?: string,
): string {
  return String(timeType ?? '').toLowerCase() === 'month'
    ? 'YYYY-MM'
    : 'YYYY-MM-DD';
}

export interface IBuildFullScreenEventDataOptions {
  excludeSet: Set<string>;
  /** 趋势图 X 轴最小日期（YYYY-MM-DD）；有值时剔除更早的 start_time */
  minAxisDate?: string;
}

/** 日期比较用 YYYY-MM-DD（支持 YYYY-MM / YYYYMMDD 片段） */
export function normalizeDateForCompare(date?: string): string {
  if (!date) {
    return '';
  }
  const matched = String(date).match(/\d{4}-\d{2}(?:-\d{2})?/);
  if (!matched) {
    return '';
  }
  return matched[0].length === 7 ? `${matched[0]}-01` : matched[0];
}

function getRecordStartDate(record: Record<string, unknown>): string {
  const raw = String(
    record.start_time ??
      record.event_start_time ??
      record.event_date ??
      '',
  );
  return normalizeDateForCompare(raw);
}

function recordMatchesFullScreenFilters(
  record: Record<string, unknown>,
  excludeSet: Set<string>,
  normalizedMinAxisDate: string,
): boolean {
  if (isEventTypeExcluded(record.event_type, excludeSet)) {
    return false;
  }
  if (normalizedMinAxisDate) {
    const startDate = getRecordStartDate(record);
    if (startDate && startDate < normalizedMinAxisDate) {
      return false;
    }
  }
  return true;
}

/** date_event_list 单日 date → YYYYMMDD（浮窗挂到对应 X 轴刻度，与接口按日下发一致） */
function getTrendDayYyyymmdd(day: { date?: unknown }): string {
  const iso = normalizeDateForCompare(String(day.date ?? ''));
  return iso ? canonicalEventDateYyyymmdd(iso) : '';
}

/** 解析 fullScreenEnhancements.excludeEventTypes */
export function resolveFullScreenExcludeEventTypes(
  enhancements?: IFullScreenExcludeEventTypesConfig,
): Set<string> {
  return normalizeExcludeEventTypes(enhancements?.excludeEventTypes);
}

/**
 * 全屏事件源：优先 date_event_list 数组；否则将 data[eventModalDataKey] 转为按日列表。
 */
export function resolveFullScreenEventSource(
  eventData: unknown,
  data?: Record<string, unknown>,
  eventModalDataKey?: string,
): unknown[] {
  if (Array.isArray(eventData) && eventData.length > 0) {
    return eventData;
  }
  if (eventModalDataKey && data) {
    const modal = (data as Record<string, unknown>)[eventModalDataKey];
    if (modal && typeof modal === 'object' && !Array.isArray(modal)) {
      return groupedModalToTrendEventList(modal as Record<string, unknown[]>);
    }
  }
  return Array.isArray(eventData) ? eventData : [];
}

/**
 * 弹窗分组 Record → 按 start_time 归日的 trend 列表（供统一过滤）。
 *
 * modal 的 key 由 resolveEventTypeDisplayOrderKey(record.event_type) 生成（字符串）；
 * 每条 record 的 event_type 通常是原始对象（{ name_cn, name_en, id, ... }）。
 *
 * 同一天可能存在多种 typeKey（多种事件类型），因此按 date → typeKey 两级聚合，
 * 每种 typeKey 生成独立的 block，避免不同类型记录混入同一 block 导致 blockEventType 错分类。
 *
 * 不用 typeKey 字符串覆写 record.event_type：
 *   - 若 record 有 event_type，保留原始对象形态，后续 getOnlyOneKey / resolveEventRecordKeyEventType 能正确处理
 *   - 若 record 无 event_type，保持 undefined，由 iterateTrendEventRecords 中的 blockEventType 兜底
 */
export function groupedModalToTrendEventList(
  modal: Record<string, unknown[]>,
): unknown[] {
  // date → (typeKey → records[])，保留各 typeKey 独立 block
  const dateMap = new Map<string, Map<string, Record<string, unknown>[]>>();
  Object.entries(modal).forEach(([typeKey, list]) => {
    (list || []).forEach(item => {
      const record = (item || {}) as Record<string, unknown>;
      const date = getRecordStartDate(record);
      if (!date) {
        return;
      }
      if (!dateMap.has(date)) {
        dateMap.set(date, new Map());
      }
      const typeMap = dateMap.get(date)!;
      if (!typeMap.has(typeKey)) {
        typeMap.set(typeKey, []);
      }
      typeMap.get(typeKey)!.push(record);
    });
  });
  return Array.from(dateMap.entries()).map(([date, typeMap]) => {
    const event_list = Array.from(typeMap.entries()).map(([blockType, records]) => ({
      event_type: blockType,
      events: records.length,
      event_record: records,
    }));
    return {
      date,
      events: event_list.reduce((sum, b) => sum + b.events, 0),
      event_list,
    };
  });
}

/**
 * 全屏统一过滤：excludeEventTypes + start_time 早于趋势 X 轴最小日的记录剔除。
 */
export function buildFullScreenEventData(
  source: unknown[],
  options: IBuildFullScreenEventDataOptions,
): unknown[] {
  if (!Array.isArray(source) || source.length === 0) {
    return [];
  }
  const { excludeSet } = options;
  const normalizedMinAxisDate = normalizeDateForCompare(options.minAxisDate);
  const cloned = cloneDeep(source) as Array<{
    date?: string;
    events?: number;
    event_list?: Array<{
      event_type?: unknown;
      event_record?: Record<string, unknown>[];
      events?: number;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  }>;

  cloned.forEach(day => {
    if (!day || !Array.isArray(day.event_list)) {
      return;
    }
    day.event_list = day.event_list
      .map(block => {
        if (!block || !Array.isArray(block.event_record)) {
          return block;
        }
        const filtered = block.event_record.filter(r =>
          recordMatchesFullScreenFilters(r, excludeSet, normalizedMinAxisDate),
        );
        return { ...block, event_record: filtered };
      })
      .filter(
        block =>
          Array.isArray(block?.event_record) && block.event_record.length > 0,
      );
    let total = 0;
    day.event_list.forEach(block => {
      const n = Array.isArray(block.event_record) ? block.event_record.length : 0;
      block.events = n;
      total += n;
    });
    day.events = total;
  });

  return cloned.filter(
    day => Array.isArray(day.event_list) && day.event_list.length > 0,
  );
}

/**
 * 将过滤后的 date_event_list 转为事件设置弹窗所需的 Record<event_type, events[]>。
 */
export function convertEventDataToModalData(
  eventData: unknown,
): Record<string, unknown[]> {
  if (eventData && typeof eventData === 'object' && !Array.isArray(eventData)) {
    return eventData as Record<string, unknown[]>;
  }
  if (!Array.isArray(eventData) || eventData.length === 0) {
    return {};
  }
  const built = buildPropsEventDataFromEventList(eventData);
  return groupRawEventListByTypeForModal(
    built.rawEventList as Array<Record<string, unknown>> | undefined,
  );
}

/** 弹窗 / 浮窗 bubbleEvent 与分组 key 对齐 */
export function resolveEventTypeKeyForUi(typeRaw: unknown): string {
  return resolveEventTypeDisplayOrderKey(typeRaw) || '';
}

/**
 * 从 Events Setting 确认后的 bubbleEvent 提取选中事件 key（与 KPI filter 层同源）。
 * 弹窗 getCallbackEventList 产出为 Record<X轴日期, 事件[]>，不能按 event_type 键过滤。
 */
export function extractSelectedEventKeysFromBubbleEvent(
  bubbleEvent: Record<string, unknown[]>,
): Set<string> {
  const keys = new Set<string>();
  Object.values(bubbleEvent).forEach(arr => {
    if (!Array.isArray(arr)) {
      return;
    }
    arr.forEach(item => {
      if (item && typeof item === 'object') {
        keys.add(getOnlyOneKey(item as Record<string, unknown>));
      }
    });
  });
  return keys;
}

/**
 * 判断单条事件是否应出现在 EventWindows。
 * @param filterCommitted 用户是否已在弹窗点过「确定」并写入 bubbleEvent
 */
export function recordPassesBubbleEventSelection(
  raw: Record<string, unknown>,
  bubbleEvent: Record<string, unknown[]>,
  filterCommitted: boolean,
  blockEventType?: unknown,
): boolean {
  const selectedKeys = extractSelectedEventKeysFromBubbleEvent(bubbleEvent);
  if (selectedKeys.size > 0) {
    return selectedKeys.has(getOnlyOneKey(raw));
  }
  if (filterCommitted) {
    return false;
  }
  const hasBubbleFilter = Object.keys(bubbleEvent).length > 0;
  if (!hasBubbleFilter) {
    return true;
  }
  const typeKey = resolveEventTypeKeyForUi(raw.event_type ?? blockEventType);
  const allowed = bubbleEvent[typeKey];
  if (
    Object.prototype.hasOwnProperty.call(bubbleEvent, typeKey) &&
    !(Array.isArray(allowed) && allowed.length > 0)
  ) {
    return false;
  }
  return true;
}

function buildEventRecordDedupeKey(record: Record<string, unknown>): string {
  const id = record.id;
  if (id !== undefined && id !== null && id !== '' && id !== 0) {
    return `id_${id}`;
  }
  return `type_${record.event_type ?? ''}_title_${record.event_title_en ?? ''}`;
}

type TNormalizedEventItem = ReturnType<typeof normalizeRecordForEventWindowsDisplay>;

/**
 * 公共迭代器：遍历 date_event_list，经 bubbleEvent 过滤和去重后，对每条通过的记录调用 callback。
 * buildEventListMapForWindows / buildPreCollapseEventCountByAxis 共享此循环骨架。
 */
function iterateTrendEventRecords(
  trendList: unknown[],
  bubbleEvent: Record<string, unknown[]>,
  bubbleFilterCommitted: boolean,
  callback: (item: TNormalizedEventItem, axisYmd: string, blockType: unknown, raw: Record<string, unknown>) => void,
): void {
  if (!Array.isArray(trendList) || trendList.length === 0) {
    return;
  }
  const seenRecordKeysByAxis = new Map<string, Set<string>>();

  trendList.forEach(day => {
    if (!day || typeof day !== 'object') {
      return;
    }
    const dayObj = day as { date?: unknown; event_list?: unknown[] };
    const dayAxisYmd = getTrendDayYyyymmdd(dayObj);
    const eventList = dayObj.event_list;
    if (!Array.isArray(eventList)) {
      return;
    }

    eventList.forEach(block => {
      if (!block || typeof block !== 'object') {
        return;
      }
      const blockObj = block as {
        event_type?: unknown;
        event_record?: Record<string, unknown>[];
      };
      const blockType = blockObj.event_type;
      (blockObj.event_record ?? []).forEach(raw => {
        if (!raw || typeof raw !== 'object') {
          return;
        }
        if (
          !recordPassesBubbleEventSelection(
            raw,
            bubbleEvent,
            bubbleFilterCommitted,
            blockType,
          )
        ) {
          return;
        }
        const item = normalizeRecordForEventWindowsDisplay(raw, {
          blockEventType: blockType,
        });
        const axisYmd =
          dayAxisYmd ||
          canonicalEventDateYyyymmdd(String(item.start_time ?? ''));
        if (!axisYmd) {
          return;
        }

        const dedupeKey = buildEventRecordDedupeKey(raw);
        if (!seenRecordKeysByAxis.has(axisYmd)) {
          seenRecordKeysByAxis.set(axisYmd, new Set());
        }
        const seenOnAxis = seenRecordKeysByAxis.get(axisYmd)!;
        if (seenOnAxis.has(dedupeKey)) {
          return;
        }
        seenOnAxis.add(dedupeKey);

        callback(item, axisYmd, blockType, raw);
      });
    });
  });
}

/**
 * date_event_list → EventWindows 用的 EventDataMap（键 YYYYMMDD，按 start_time 归轴）。
 * 与 EventMetricTrend 的 eventListForKpiEventWindows / eventWindowsData.json 形态一致。
 */
export function buildEventListMapForWindows(
  trendList: unknown[],
  bubbleEvent: Record<string, unknown[]>,
  eventTypeMaxCount: number,
  eventTypeBarColorMap?: TEventTypeBarColorMapConfig,
  bubbleFilterCommitted = false,
): EventDataMap {
  const map: EventDataMap = {};
  const typeCountByAxis = new Map<string, Map<string, number>>();

  iterateTrendEventRecords(trendList, bubbleEvent, bubbleFilterCommitted, (item, axisYmd, blockType) => {
    const typeKey = resolveEventTypeKeyForUi(item.event_type ?? blockType);
    if (!typeCountByAxis.has(axisYmd)) {
      typeCountByAxis.set(axisYmd, new Map());
    }
    const perType = typeCountByAxis.get(axisYmd)!;
    const typeCnt = perType.get(typeKey) ?? 0;
    if (eventTypeMaxCount > 0 && typeCnt >= eventTypeMaxCount) {
      return;
    }
    perType.set(typeKey, typeCnt + 1);
    if (!map[axisYmd]) {
      map[axisYmd] = [];
    }
    map[axisYmd].push(item);
  });

  return (
    applyEventTypeBarColorMapToEventDataMap(map, eventTypeBarColorMap) ?? map
  );
}

/**
 * 浮窗展示前各轴事件总条数（与 buildEventListMapForWindows 同过滤/去重，不受 eventTypeMaxCount 限制）。
 * 键为 YYYYMMDD（按 start_time 归轴）。
 */
export function buildPreCollapseEventCountByAxis(
  trendList: unknown[],
  bubbleEvent: Record<string, unknown[]>,
  bubbleFilterCommitted = false,
): Record<string, number> {
  const counts: Record<string, number> = {};

  iterateTrendEventRecords(trendList, bubbleEvent, bubbleFilterCommitted, (_, axisYmd) => {
    counts[axisYmd] = (counts[axisYmd] ?? 0) + 1;
  });

  return counts;
}

export interface IResolvePreCollapseEventCountOptions {
  dateType?: TOverviewEventDateType;
}

/**
 * 按 X 轴类目解析合并前事件条数。
 * counts 键为 YYYYMMDD（按日）；月轴 YYYY-MM、周轴需汇总桶内各日计数。
 */
export function resolvePreCollapseEventCount(
  counts: Record<string, number>,
  xAxisValue: string,
  options?: IResolvePreCollapseEventCountOptions,
): number {
  const dateType = options?.dateType;
  const axisTrim = String(xAxisValue ?? '').trim();

  const direct = counts[xAxisValue];
  if (typeof direct === 'number') {
    return direct;
  }

  // 月粒度 X 轴 YYYY-MM：canonicalEventDateYyyymmdd 无法解析，需按月汇总
  if (dateType === 'monthly' && /^\d{4}-\d{2}$/.test(axisTrim)) {
    return Object.entries(counts).reduce((sum, [key, n]) => {
      const storageYmd = canonicalEventDateYyyymmdd(key);
      return storageYmd && storageYmdMatchesMonthAxis(storageYmd, axisTrim)
        ? sum + n
        : sum;
    }, 0);
  }

  const ymd = canonicalEventDateYyyymmdd(xAxisValue);
  if (ymd && counts[ymd] !== undefined) {
    return counts[ymd];
  }
  if (/^\d{8}$/.test(ymd)) {
    const hyphen = `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
    if (counts[hyphen] !== undefined) {
      return counts[hyphen];
    }
  }

  // 周粒度：事件按日计数，X 轴为周内代表日，需汇总同 ISO 周
  if (dateType === 'weekly' && ymd) {
    return Object.entries(counts).reduce((sum, [key, n]) => {
      const storageYmd = canonicalEventDateYyyymmdd(key);
      return storageYmd && isSameIsoWeekYmd(storageYmd, ymd) ? sum + n : sum;
    }, 0);
  }

  return 0;
}

/** storageYmd 是否落在月粒度 X 轴类目 YYYY-MM 内 */
function storageYmdMatchesMonthAxis(
  storageYmd: string,
  axisMonth: string,
): boolean {
  const ym = String(axisMonth ?? '')
    .trim()
    .replace(/-/g, '');
  if (!/^\d{6}$/.test(ym) || !storageYmd) {
    return false;
  }
  return storageYmd.startsWith(ym);
}

function isSameIsoWeekYmd(aYmd: string, bYmd: string): boolean {
  const a = moment(aYmd, 'YYYYMMDD');
  const b = moment(bYmd, 'YYYYMMDD');
  if (!a.isValid() || !b.isValid()) {
    return false;
  }
  return a.isoWeekYear() === b.isoWeekYear() && a.isoWeek() === b.isoWeek();
}

/** 周桶 date（如周日）与 X 轴刻度（如周一）可能不同日但同属一周 */
function findAxisKeyInSameIsoWeek(
  storageYmd: string,
  xAxisData: string[],
): string | undefined {
  if (!storageYmd) {
    return undefined;
  }
  let best: { axisKey: string; diffDays: number; } | undefined;
  xAxisData.forEach(ax => {
    const axYmd = canonicalEventDateYyyymmdd(ax);
    if (!axYmd || !isSameIsoWeekYmd(storageYmd, axYmd)) {
      return;
    }
    const diffDays = Math.abs(
      moment(storageYmd, 'YYYYMMDD').diff(moment(axYmd, 'YYYYMMDD'), 'days'),
    );
    if (!best || diffDays < best.diffDays) {
      best = { axisKey: ax, diffDays };
    }
  });
  return best?.axisKey;
}

function findAxisKeyForStorageYmd(
  storageYmd: string,
  xAxisData: string[],
  dateType?: TOverviewEventDateType,
): string | undefined {
  if (!storageYmd) {
    return undefined;
  }
  const exact = xAxisData.find(ax => {
    const candidates = expandDateKeyCandidates(ax);
    return candidates.some(
      c => canonicalEventDateYyyymmdd(c) === storageYmd,
    );
  });
  if (exact) {
    return exact;
  }
  const monthMatch = xAxisData.find(ax => {
    const axTrim = String(ax ?? '').trim();
    if (/^\d{4}-\d{2}$/.test(axTrim)) {
      return storageYmdMatchesMonthAxis(storageYmd, axTrim);
    }
    return false;
  });
  if (monthMatch) {
    return monthMatch;
  }
  if (dateType === 'weekly') {
    return findAxisKeyInSameIsoWeek(storageYmd, xAxisData);
  }
  return undefined;
}

export interface IAlignEventDataMapToXAxisOptions {
  dateType?: TOverviewEventDateType;
}

/**
 * EventWindows 的 eventList 键须与 echarts xAxis.data 类目一致（多为 YYYY-MM-DD）。
 * 内部存储可用 YYYYMMDD，传入 EventWindows 前按 xAxis 对齐。
 */
export function alignEventDataMapKeysToXAxis(
  map: EventDataMap,
  xAxisData: string[],
  options?: IAlignEventDataMapToXAxisOptions,
): EventDataMap {
  if (!xAxisData.length) {
    return map;
  }
  const dateType = options?.dateType;
  const out: EventDataMap = {};
  Object.entries(map).forEach(([storageKey, items]) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const storageYmd = canonicalEventDateYyyymmdd(storageKey);
    const axisKey = findAxisKeyForStorageYmd(
      storageYmd,
      xAxisData,
      dateType,
    );
    if (!axisKey) {
      return;
    }
    if (!out[axisKey]) {
      out[axisKey] = [];
    }
    out[axisKey].push(...items);
  });
  return out;
}
